import React, { useEffect, useState } from "react";
import { Alert, Box, Card, Chip, Grid, Typography } from "@mui/material";
import { Add, PlaylistAdd } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import {
  applyHolidayFilters,
  bulkApplyAvailability,
  createHolidayAbsence,
  deleteHolidayAbsence,
  updateHolidayAbsence,
  updateHolidayFilters,
  upsertAvailabilityCell,
} from "../../redux/Thunk/holidayCalendarModule/HolidayplannerThunk";
import { getGeneralMasterDropDownList } from "../../redux/Thunk/DashboardModule/DashboardThunk";
import Btn from "../Ui_elements/Btn";
import AvailabilityMatrix from "./AvailabilityMatrix/AvailabilityMatrix";
import AvailabilityFilters from "./AvailabilityFilters";
import CellEditorPopover from "./CellEditorPopover";
import RangeModal from "./RangeModal";
import BulkApplyDrawer from "./BulkApplyDrawer";
import { getPeriodRange } from "./PeriodNavigator";
import { STATUS_OPTIONS } from "./statusConfig";

const HolidayPlanner = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state?.root?.authUser);
  const isFreelancer = user?.iFkUserTypeId === 2;
  const loginUserId = user?.iUserId;

  const { matrix, filters, error, matrixLoading } = useSelector(state => state.root.HolidayPlannerModule);
  const { dropdowns } = useSelector(state => state.root.DashboardModule);
  const { languages = [], resource = [] } = dropdowns;

  const [showFilters, setShowFilters] = useState(false);
  const [rangeModalOpen, setRangeModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState(false);
  const [cellEditorOpen, setCellEditorOpen] = useState(false);
  const [cellEditor, setCellEditor] = useState({
    linguist: null,
    date: null,
    cell: null,
  });
  const [savingCell, setSavingCell] = useState(false);

  const applyPeriod = (viewMode, anchorDate) => {
    const range = getPeriodRange(viewMode, anchorDate);
    dispatch(
      updateHolidayFilters({
        viewMode,
        anchorDate: range.anchorDate,
        startDate: range.startDate,
        endDate: range.endDate,
      })
    );
    dispatch(applyHolidayFilters());
  };

  useEffect(() => {
    const viewMode = filters.viewMode || "month";
    const range = getPeriodRange(viewMode, filters.anchorDate || dayjs());

    if (isFreelancer) {
      dispatch(
        updateHolidayFilters({
          resource: loginUserId,
          viewMode,
          anchorDate: filters.anchorDate || range.anchorDate,
          startDate: filters.startDate || range.startDate,
          endDate: filters.endDate || range.endDate,
        })
      );
      dispatch(getGeneralMasterDropDownList({ languages: true }));
    } else {
      dispatch(
        updateHolidayFilters({
          viewMode,
          anchorDate: filters.anchorDate || range.anchorDate,
          startDate: filters.startDate || range.startDate,
          endDate: filters.endDate || range.endDate,
        })
      );
      dispatch(getGeneralMasterDropDownList({ languages: true, resource: true }));
    }
    dispatch(applyHolidayFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isFreelancer, loginUserId]);

  const handleViewModeChange = viewMode => {
    applyPeriod(viewMode, filters.anchorDate || dayjs().format("YYYY-MM-DD"));
  };

  const handleNavigate = direction => {
    const viewMode = filters.viewMode || "month";
    const unit = viewMode === "week" ? "week" : "month";
    const anchor = dayjs(filters.anchorDate || undefined).add(direction, unit);
    applyPeriod(viewMode, anchor.format("YYYY-MM-DD"));
  };

  const handleToday = () => {
    applyPeriod(filters.viewMode || "month", dayjs().format("YYYY-MM-DD"));
  };

  const resourceOptions = resource.map(r => ({
    value: r.iUserId,
    label: r.vUserName,
  }));
  const languagesOptions = languages.map(l => ({
    value: l.lang_id,
    label: l.title,
  }));

  const handleClearFilters = () => {
    const range = getPeriodRange("month", dayjs());
    dispatch(
      updateHolidayFilters({
        sourceLanguage: "",
        targetLanguage: "",
        resource: isFreelancer ? loginUserId : "",
        status: "",
        showAbsentOnly: false,
        showPresentOnly: false,
        viewMode: "month",
        anchorDate: range.anchorDate,
        startDate: range.startDate,
        endDate: range.endDate,
      })
    );
    dispatch(applyHolidayFilters());
  };

  const handleCellClick = (_event, linguist, date, cell) => {
    if (isFreelancer && linguist.id !== loginUserId) return;
    setCellEditor({
      linguist,
      date,
      cell: cell || null,
    });
    setCellEditorOpen(true);
  };

  const handleCellSave = async data => {
    if (!cellEditor.linguist || !cellEditor.date) return;
    setSavingCell(true);
    try {
      await dispatch(
        upsertAvailabilityCell({
          user_id: cellEditor.linguist.id,
          date: cellEditor.date,
          ...data,
        })
      ).unwrap();
      setCellEditorOpen(false);
      setCellEditor({ linguist: null, date: null, cell: null });
    } catch {
      // error handled in thunk
    } finally {
      setSavingCell(false);
    }
  };

  const handleCellClear = async () => {
    if (!cellEditor.linguist || !cellEditor.date) return;
    setSavingCell(true);
    try {
      await dispatch(
        upsertAvailabilityCell({
          user_id: cellEditor.linguist.id,
          date: cellEditor.date,
          eStatus: "CLEAR",
        })
      ).unwrap();
      setCellEditorOpen(false);
      setCellEditor({ linguist: null, date: null, cell: null });
    } catch {
      // error handled in thunk
    } finally {
      setSavingCell(false);
    }
  };

  const handleRangeSave = payload => {
    if (editingRecord?.absentId) {
      dispatch(updateHolidayAbsence({ id: editingRecord.absentId, payload }));
    } else {
      dispatch(createHolidayAbsence(payload));
    }
    setRangeModalOpen(false);
    setEditingRecord(null);
  };

  const handleRangeDelete = () => {
    if (editingRecord?.absentId) {
      dispatch(deleteHolidayAbsence(editingRecord.absentId));
      setRangeModalOpen(false);
      setEditingRecord(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
      <Box sx={{ p: { xs: 1, md: 3 } }}>
        <Card sx={{ mb: 3, p: 3, border: 1, borderColor: "divider" }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Holiday Planner
              </Typography>
              <Grid container spacing={1}>
                {STATUS_OPTIONS.map(opt => (
                  <Grid item key={opt.value}>
                    <Chip
                      label={`${opt.short} — ${opt.label}`}
                      size="small"
                      sx={{
                        bgcolor: opt.bg,
                        color: opt.color,
                        fontWeight: 600,
                        border: `1px solid ${opt.color}`,
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: "flex", gap: 1, justifyContent: { md: "flex-end" }, flexWrap: "wrap" }}
            >
              {!isFreelancer && (
                <Btn
                  variant="outlined"
                  startIcon={<PlaylistAdd />}
                  onClick={() => setBulkDrawerOpen(true)}
                >
                  Bulk Apply
                </Btn>
              )}
              <Btn
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setEditingRecord(null);
                  setRangeModalOpen(true);
                }}
              >
                Add Range
              </Btn>
            </Grid>
          </Grid>
        </Card>

        {error.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.join(", ")}
          </Alert>
        )}

        <AvailabilityFilters
          isFreelancer={isFreelancer}
          resourceOptions={resourceOptions}
          languagesOptions={languagesOptions}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          onClear={handleClearFilters}
        />

        <Card sx={{ p: 2 }}>
          <AvailabilityMatrix
            dates={matrix.dates}
            linguists={matrix.linguists}
            cells={matrix.cells}
            onCellClick={handleCellClick}
            readOnly={false}
            viewMode={filters.viewMode || "month"}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onViewModeChange={handleViewModeChange}
            onNavigate={handleNavigate}
            onToday={handleToday}
            loading={matrixLoading}
          />
        </Card>

        <CellEditorPopover
          open={cellEditorOpen}
          onClose={() => {
            setCellEditorOpen(false);
            setCellEditor({ linguist: null, date: null, cell: null });
          }}
          cell={cellEditor.cell}
          linguistName={cellEditor.linguist?.name}
          date={cellEditor.date}
          onSave={handleCellSave}
          onClear={handleCellClear}
          saving={savingCell}
        />

        <RangeModal
          open={rangeModalOpen}
          onClose={() => {
            setRangeModalOpen(false);
            setEditingRecord(null);
          }}
          isFreelancer={isFreelancer}
          loginUserId={loginUserId}
          resource={resource}
          editingRecord={editingRecord}
          onSave={handleRangeSave}
          onDelete={handleRangeDelete}
        />

        {!isFreelancer && (
          <BulkApplyDrawer
            open={bulkDrawerOpen}
            onClose={() => setBulkDrawerOpen(false)}
            linguists={matrix.linguists}
            applying={false}
            onApply={payload => {
              dispatch(bulkApplyAvailability(payload)).then(() => setBulkDrawerOpen(false));
            }}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default HolidayPlanner;
